export type LinuxPackageParts = {packageName: string; distro: string; distroVersion: string};

/**
 * Some Linux distros will have major + minor versions, but the Project locator embedded into our Vulnerability identifier
 * will be lacking the minor version. This is a list of the distros that should be compared by their major version only,
 * so that we can perform additional logic such as stripping out the minor version or querying for Projects/Revisions
 * differently.
 */
export const majorVersionedDistros = ['redhat', 'centos', 'debian'];

/** Like the `majorVersionedDistros, but includes the minor version and ignore the patch version. */
export const minorVersionedDistros = ['alpine'];

const LINUX_DELIMITER = '#';

/**
 * Parses linux revision, splitting on LINUX_DELIMITER. Returns unparsed revisions on failure.
 *
 * @param unparsedRevision string
 * @returns arch + revision or string on failure
 */
export function parseLinuxRevision(unparsedRevision: string): { arch: string; revision: string } | string {
  const [arch, revision] = unparsedRevision.split(LINUX_DELIMITER);
  if (!arch || !revision) {
    return unparsedRevision;
  }

  return { arch, revision };
}

/**
 * Parses linux package, splitting on LINUX_DELIMITER. Returns unparsed revision on failure.
 *
 * @param unparsedPackage string
 * @returns packageName + distro + distroVersion or string on failure
 */
export function parseLinuxPackage(
  unparsedPackage: string,
): LinuxPackageParts | string {
  const [packageName, distro, distroVersion] = unparsedPackage.split(LINUX_DELIMITER);
  if (!packageName || !distro || !distroVersion) {
    return unparsedPackage;
  }

  return { packageName, distro, distroVersion };
}

const betaSupportedOSes: string [] = [
  'sles',
  'fedora',
];

const unAliasNames = (alias: string): string => {
  if (alias === 'ol') {
    return 'oraclelinux';
  }
  if (alias === 'rhel') {
    return 'redhat';
  }

  return alias;
};

const supportedOSes: string[] = [
  'alpine',
  'centos',
  'debian',
  'redhat',
  'ubuntu',
  'oraclelinux',
  'busybox',
].concat(betaSupportedOSes);

// This version of the function is primarily for use in the frontend.
// Use 'modules/containerScanning/utils.ts' which also as an 'isOSSupported' method.
export const isOSSupported = (os: string): boolean => {
  const osName = unAliasNames(os);

  return supportedOSes.includes(osName);
};

export const isOSinBetaSupport = (os: string): boolean => betaSupportedOSes.includes(unAliasNames(os));
